import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import ActivityModal from '../components/crm/ActivityModal';
import { formatDate, todayStr } from '../components/crm/crmConstants';

export default function Tasks() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [mineOnly, setMineOnly] = useState(true);
  const [showDone, setShowDone] = useState(false);
  const [modal, setModal] = useState(null); // null | { activity? }

  const fetchData = useCallback(async () => {
    const res = await api.get('/api/crm/activities', {
      params: { tasks_only: true, open_only: !showDone, assigned_to_me: mineOnly },
    });
    setTasks(res.data);
  }, [mineOnly, showDone]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleDone = async (t) => {
    await api.put(`/api/crm/activities/${t.id}`, { is_done: !t.is_done });
    fetchData();
  };

  const handleDelete = async (t) => {
    if (!window.confirm('Görevi silmek istediğinize emin misiniz?')) return;
    await api.delete(`/api/crm/activities/${t.id}`);
    fetchData();
  };

  const today = todayStr();
  const groups = [
    { key: 'overdue', label: 'Gecikmiş', className: 'text-red-600', items: tasks.filter(t => !t.is_done && t.due_date && t.due_date < today) },
    { key: 'today', label: 'Bugün', className: 'text-amber-600', items: tasks.filter(t => !t.is_done && t.due_date === today) },
    { key: 'upcoming', label: 'Yaklaşan', className: 'text-gray-700', items: tasks.filter(t => !t.is_done && t.due_date && t.due_date > today) },
    { key: 'nodate', label: 'Tarihsiz', className: 'text-gray-500', items: tasks.filter(t => !t.is_done && !t.due_date) },
    { key: 'done', label: 'Tamamlanan', className: 'text-green-600', items: tasks.filter(t => t.is_done) },
  ].filter(g => g.items.length > 0);

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold text-gray-800">Görevler</h2>
        <button onClick={() => setModal({})} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-medium">+ Yeni Görev</button>
      </div>

      <div className="flex items-center gap-4 mb-4">
        <div className="inline-flex rounded-lg border bg-white overflow-hidden text-sm">
          <button onClick={() => setMineOnly(true)} className={`px-3 py-1.5 ${mineOnly ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>Bana atananlar</button>
          <button onClick={() => setMineOnly(false)} className={`px-3 py-1.5 ${!mineOnly ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>Tümü</button>
        </div>
        <label className="text-sm text-gray-600 flex items-center gap-1">
          <input type="checkbox" checked={showDone} onChange={e => setShowDone(e.target.checked)} /> Tamamlananları göster
        </label>
      </div>

      {groups.map(g => (
        <div key={g.key} className="mb-6">
          <h3 className={`text-sm font-semibold uppercase mb-2 ${g.className}`}>{g.label} <span className="text-gray-400 font-normal">({g.items.length})</span></h3>
          <div className="bg-white rounded-lg shadow divide-y">
            {g.items.map(t => (
              <div key={t.id} className="px-4 py-3 flex items-start gap-3 hover:bg-gray-50">
                <input type="checkbox" className="mt-1" checked={t.is_done} onChange={() => toggleDone(t)} />
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-medium ${t.is_done ? 'line-through text-gray-400' : 'text-gray-800'}`}>{t.subject}</div>
                  {t.description && <div className="text-sm text-gray-500 truncate">{t.description}</div>}
                  <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-x-3">
                    <button onClick={() => navigate(`/customers/${t.customer_id}`)} className="text-blue-600 hover:underline">{t.customer_name}</button>
                    {t.opportunity_title && <span>💼 {t.opportunity_title}</span>}
                    {t.quote_number && <button onClick={() => navigate(`/quotes/${t.quote_id}`)} className="text-blue-500 hover:underline">📄 {t.quote_number}</button>}
                    {t.contact_name && <span>👤 {t.contact_name}</span>}
                    {!mineOnly && t.assigned_to_name && <span>→ {t.assigned_to_name}</span>}
                  </div>
                </div>
                <div className="text-sm text-gray-600 whitespace-nowrap">{t.due_date ? formatDate(t.due_date) : ''}</div>
                <div className="flex gap-2 text-xs whitespace-nowrap">
                  <button onClick={() => setModal({ activity: t })} className="text-blue-600 hover:text-blue-800">Düzenle</button>
                  <button onClick={() => handleDelete(t)} className="text-red-500 hover:text-red-700">Sil</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {groups.length === 0 && (
        <div className="bg-white rounded-lg shadow text-center py-12 text-gray-400">Görev yok 🎉</div>
      )}

      {modal && (
        <ActivityModal
          activity={modal.activity}
          defaults={{ type: 'task' }}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); fetchData(); }}
        />
      )}
    </div>
  );
}
